import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import PropTypes from 'prop-types';

const RealTimeContext = createContext();

export const useRealTime = () => {
  const context = useContext(RealTimeContext);
  if (!context) {
    throw new Error('useRealTime must be used within a RealTimeProvider');
  }
  return context;
};

export const RealTimeProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [liveStats, setLiveStats] = useState({
    totalViews: 0,
    totalArticles: 0,
    publishedArticles: 0,
    totalUsers: 0,
    recentViews: []
  });

  useEffect(() => {
    // Always use window.location.origin for Socket.io in production/live.
    // In local dev, we can use the backend URL from env.
    const SOCKET_URL = import.meta.env.VITE_BACKEND_URL || window.location.origin;
    
    console.log('Connecting to Socket.io at:', SOCKET_URL);
    
    const newSocket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setConnected(true);
      // Subscribe to analytics updates
      newSocket.emit('subscribe-analytics');
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnected(false);
    });

    newSocket.on('stats-update', (data) => {
      console.log('Real-time stats update:', data);
      setLiveStats(prev => ({
        ...prev,
        ...data
      }));
    });

    newSocket.on('view-update', (data) => {
      console.log('Real-time view update:', data);
      setLiveStats(prev => ({
        ...prev,
        totalViews: data.totalViews || prev.totalViews,
        recentViews: [data.article, ...prev.recentViews.slice(0, 9)]
      }));
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // Manual refresh function
  const refreshStats = useCallback(() => {
    if (socket && connected) {
      socket.emit('request-stats');
    }
  }, [socket, connected]);

  const value = {
    socket,
    connected,
    liveStats,
    refreshStats
  };

  return (
    <RealTimeContext.Provider value={value}>
      {children}
    </RealTimeContext.Provider>
  );
};

RealTimeProvider.propTypes = {
  children: PropTypes.node.isRequired
};
